import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { executeSoon } from '@streamr/tv-utils';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Popup, TopCenter } from '../../utils/popups';
import { ChangeBackgroundPopup } from '../ChangeBackgroundPopup';
import { AbstractPopup, DismissalReason, } from './AbstractPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { PopupCssPreset } from './Popup.styles';
const AUTOMATIC_LAYOUTS = [
    'auto',
    'side-by-side',
    'focus-right',
    'focus-left-stack',
    'grid-2x2',
    'focus-right-stack',
    'three-column',
    'spotlight',
    'gallery',
];
const LAYOUT_LABELS = {
    auto: '',
    'side-by-side': '',
    'focus-right': '',
    'focus-left-stack': '',
    'grid-2x2': '',
    'focus-right-stack': '',
    'three-column': '',
    spotlight: '',
    gallery: '',
    manual: '',
};
function LayoutOptionItem({ layout, selectedLayout, onSelect, }) {
    return (_jsxs(LayoutOption, { "$selected": layout === selectedLayout, onClick: () => onSelect(layout), title: LAYOUT_LABELS[layout] || layout.replace(/-/g, ' '), children: [_jsx(LayoutThumbnail, { layout: layout }), LAYOUT_LABELS[layout] && (_jsx(LayoutLabel, { children: LAYOUT_LABELS[layout] }))] }));
}
function LayoutPopup({ resolve, selectedLayout, onLayoutSelect, currentBackground, onSelectBackground, onUploadBackgroundImage, ...props }) {
    const [current, setCurrent] = useState(selectedLayout);
    const [showBgPicker, setShowBgPicker] = useState(false);
    const handleSelect = (layout) => {
        setCurrent(layout);
        onLayoutSelect?.(layout);
        resolve(layout);
    };
    return (_jsx(LayoutPopupRoot, { translateFn: TopCenter, ...props, children: _jsx(ScrollablePopupContainer, { fullscreenOnly: false, onCloseButtonClick: () => {
                props.reject(DismissalReason.CloseButton);
            }, children: _jsxs(PopupContent, { children: [_jsx(Title, { children: "Layouts" }), _jsx(SectionLabel, { children: "Automatic" }), _jsx(LayoutGrid, { children: AUTOMATIC_LAYOUTS.map((layout) => (_jsx(LayoutOptionItem, { layout: layout, selectedLayout: current, onSelect: handleSelect }, layout))) }), _jsx(SectionLabel, { children: "Manual" }), _jsx(LayoutGrid, { children: _jsx(LayoutOptionItem, { layout: "manual", selectedLayout: current, onSelect: handleSelect }) }), onSelectBackground && (_jsxs(_Fragment, { children: [_jsx(PopupDivider, {}), _jsxs(BackgroundToggle, { onClick: () => setShowBgPicker((v) => !v), children: [_jsx(BackgroundIconSvg, {}), _jsx("span", { children: "Background" }), _jsxs(Chevron, { "$open": showBgPicker, children: [_jsx("title", { children: "Toggle" }), _jsx("path", { d: "M6 9l6 6 6-6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", fill: "none" })] })] }), showBgPicker && (_jsx(ChangeBackgroundPopup, { inline: true, currentBackground: currentBackground, onSelectBackground: onSelectBackground, onUploadImage: (file) => onUploadBackgroundImage?.(file), onClose: () => setShowBgPicker(false) }))] }))] }) }) }));
}
function BackgroundIconSvg() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-label": "Background", children: [_jsx("rect", { x: "1.5", y: "2.5", width: "13", height: "11", rx: "1.5", stroke: "currentColor", strokeWidth: "1.2" }), _jsx("path", { d: "M1.5 10.5L5 7.5L8 10L11 7.5L14.5 10.5", stroke: "currentColor", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("circle", { cx: "10.5", cy: "5.5", r: "1.25", stroke: "currentColor", strokeWidth: "1" })] }));
}
function LayoutThumbnail({ layout }) {
    switch (layout) {
        case 'auto':
            return (_jsx(ThumbnailGrid, { children: _jsx(ThumbnailCell, { style: { gridColumn: '1 / -1', gridRow: '1 / -1' } }) }));
        case 'side-by-side':
            return (_jsxs(ThumbnailGrid, { "$cols": 2, children: [_jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {})] }));
        case 'focus-right':
            return (_jsxs(ThumbnailGrid, { "$cols": 3, children: [_jsx(ThumbnailCell, { style: { gridColumn: '1 / 3', gridRow: '1 / -1' } }), _jsx(ThumbnailCell, { style: { gridRow: '1 / -1' } })] }));
        case 'focus-left-stack':
            return (_jsxs(ThumbnailGrid, { "$cols": 3, "$rows": 2, children: [_jsx(ThumbnailCell, { style: { gridRow: '1 / -1' } }), _jsx(ThumbnailCell, { style: { gridColumn: '2 / -1', gridRow: '1 / 2' } }), _jsx(ThumbnailCell, { style: { gridColumn: '2 / -1', gridRow: '2 / 3' } })] }));
        case 'grid-2x2':
            return (_jsxs(ThumbnailGrid, { "$cols": 2, "$rows": 2, children: [_jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {})] }));
        case 'focus-right-stack':
            return (_jsxs(ThumbnailGrid, { "$cols": 3, "$rows": 2, children: [_jsx(ThumbnailCell, { style: { gridColumn: '1 / 3', gridRow: '1 / -1' } }), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {})] }));
        case 'three-column':
            return (_jsxs(ThumbnailGrid, { "$cols": 3, children: [_jsx(ThumbnailCell, { style: { gridRow: '1 / -1' } }), _jsx(ThumbnailCell, { style: { gridRow: '1 / -1' } }), _jsx(ThumbnailCell, { style: { gridRow: '1 / -1' } })] }));
        case 'spotlight':
            return (_jsxs(ThumbnailGrid, { "$cols": 3, "$rows": 3, children: [_jsx(ThumbnailCell, { style: { gridColumn: '1 / -1', gridRow: '1 / 3' } }), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {})] }));
        case 'gallery':
            return (_jsxs(ThumbnailGrid, { "$cols": 3, "$rows": 2, children: [_jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {}), _jsx(ThumbnailCell, {})] }));
        case 'manual':
            return (_jsx(ManualThumbnail, { children: _jsxs(ManualSlot, { children: [_jsx(HandleDot, { style: { top: -3, left: -3 } }), _jsx(HandleDot, { style: { top: -3, right: -3 } }), _jsx(HandleDot, { style: { bottom: -3, left: -3 } }), _jsx(HandleDot, { style: { bottom: -3, right: -3 } }), _jsxs(MoveIcon, { viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("title", { children: "Move" }), _jsx("path", { d: "M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z", fill: "currentColor" })] })] }) }));
    }
}
const LayoutPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    @media (min-width: 30rem) {
        ${PopupCssPreset.AnchoredTopCenter};
        --Popup_Padding: 1rem;
        --Popup_Width: 22rem;
    }
`;
const PopupContent = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
`;
const Title = styled.div `
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    padding: 0 0.25rem;
`;
const SectionLabel = styled.div `
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.25rem 0.25rem 0;
`;
const LayoutGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
`;
const LayoutOption = styled.button `
    all: unset;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.06);
    border: 2px solid transparent;
    transition:
        background 150ms,
        border-color 150ms;

    ${({ $selected }) => $selected &&
    css `
            background: rgba(255, 255, 255, 0.22);
            border-color: rgba(255, 255, 255, 0.5);
        `}

    &:hover {
        background: rgba(255, 255, 255, 0.12);
    }
`;
const LayoutLabel = styled.span `
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
`;
const ThumbnailGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(${({ $cols = 1 }) => $cols}, 1fr);
    grid-template-rows: repeat(${({ $rows = 1 }) => $rows}, 1fr);
    gap: 2px;
    width: 100%;
    aspect-ratio: 16 / 10;
`;
const ThumbnailCell = styled.div `
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
    min-height: 0;
`;
const ManualThumbnail = styled.div `
    width: 100%;
    aspect-ratio: 16 / 10;
    display: flex;
    align-items: center;
    justify-content: center;
`;
const ManualSlot = styled.div `
    position: relative;
    width: 60%;
    height: 65%;
    background: rgba(255, 255, 255, 0.2);
    border: 1.5px solid rgba(255, 255, 255, 0.45);
    border-radius: 2px;
`;
const HandleDot = styled.div `
    position: absolute;
    width: 6px;
    height: 6px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
`;
const MoveIcon = styled.svg `
    position: absolute;
    inset: 0;
    margin: auto;
    width: 40%;
    height: 40%;
    color: rgba(255, 255, 255, 0.5);
`;
const PopupDivider = styled.div `
    height: 1px;
    background: rgba(255, 255, 255, 0.12);
    margin: 0.25rem 0;
`;
const BackgroundToggle = styled.button `
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.25rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
    border-radius: 0.375rem;

    &:hover {
        color: rgba(255, 255, 255, 1);
    }

    svg:first-child {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }
`;
const Chevron = styled.svg.attrs({
    viewBox: '0 0 24 24',
    width: 14,
    height: 14,
}) `
    margin-left: auto;
    color: rgba(255, 255, 255, 0.5);
    transition: transform 150ms;
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;
const layoutPopup = Popup.create(LayoutPopup);
export function showLayoutPopup({ anchorRef, selectedLayout, onLayoutSelect, currentBackground, onSelectBackground, onUploadBackgroundImage, }) {
    executeSoon(async () => {
        try {
            await layoutPopup.pop({
                anchorRef,
                selectedLayout,
                onLayoutSelect,
                currentBackground,
                onSelectBackground,
                onUploadBackgroundImage,
            });
        }
        catch (_) {
            // dismissed
        }
    });
}
